<script setup>
import { ref, onMounted, watch } from 'vue'
import apiClient from '@/api'
import Modal from 'bootstrap/js/dist/modal'
import AppLoader from '@/components/AppLoader.vue'
import Vue3EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import { webAlert } from '@/utils/dialog'

// --- 1. Konfiguracja Easy Data Table ---
const headers = [
    { text: 'ID Portalu', value: 'portalID', sortable: true, width: 100 },
    { text: 'Portal / Integracja', value: 'name', sortable: true },
    { text: 'Login / ID konta', value: 'login', sortable: true },
    { text: '', value: 'actions', width: 80 }
]

const items = ref([])
const loading = ref(false)
const saving = ref(false)
const savingTXT = ref('')
const searchValue = ref('')

const serverItemsLength = ref(0)
const serverOptions = ref({
    page: 1,
    rowsPerPage: 10,
    sortBy: 'portalID',
    sortType: 'asc'
})

// --- 2. Pobieranie danych z API ---
const loadData = async () => {
    loading.value = true
    try {
        const response = await apiClient.post(
            {
                method: 'post',
                action: 'get-export-configs',
                page: serverOptions.value.page,
                limit: serverOptions.value.rowsPerPage,
                sortBy: serverOptions.value.sortBy,
                sortType: serverOptions.value.sortType,
                search: searchValue.value
            }
        )

        items.value = Array.isArray(response.data?.data) ? response.data.data : []
        serverItemsLength.value = response.data?.total || 0
    } catch (error) {
        webAlert('Błąd podczas ładowania danych z serwera.')
    } finally {
        loading.value = false
    }
}

watch(
    serverOptions,
    () => {
        loadData()
    },
    { deep: true }
)

let searchTimeout = null
const handleSearch = () => {
    clearTimeout(searchTimeout)
    searchTimeout = setTimeout(() => {
        serverOptions.value.page = 1
        loadData()
    }, 400)
}

// --- 3. Obsługa Modala i Formularza ---
const modalRef = ref(null)
const formRef = ref(null)
let modalInstance = null

const formTitle = ref('Edycja integracji')
const showPassword = ref(false)

const formData = ref({
    ID: '',
    portalID: '',
    name: '',
    login: '',
    pass: ''
})

const openEditModal = async (portalID) => {
    savingTXT.value = 'Pobieranie danych ...'
    saving.value = true
    showPassword.value = false // reset widoczności hasła przy otwarciu
    try {
        const response = await apiClient.post({
                action: 'get-export-configs',
                portalID: portalID
        })

        const result = await response.data
        const item = Array.isArray(result.data) ? result.data[0] : result.data

        formTitle.value = `Konfiguracja serwisu: ${item.name}`
        formData.value = {
            ID: item.ID || '',
            portalID: item.portalID,
            name: item.name,
            login: item.login || '',
            pass: item.pass || item.password || ''
        }
        modalInstance.show()
    } catch (error) {
        saving.value = false; 
        webAlert('Błąd podczas pobierania danych integracji.')
    } finally {
        saving.value = false
    }
}

const handleSubmit = async () => {
    savingTXT.value = 'Zapisywanie ...'
    saving.value = true
    try {
        const response = await apiClient.post({
                action: 'save-export-config',
                id: formData.value.ID,
                portalID: formData.value.portalID,
                login: formData.value.login,
                pass: formData.value.pass
            }
        )

        modalInstance.hide()
        saving.value = false
        await loadData()

        webAlert(response.data)
    } catch (error) {
        saving.value = false
        webAlert('Błąd podczas zapisywania konfiguracji.')
    }
}

onMounted(() => {
    modalInstance = new Modal(modalRef.value)

    modalRef.value.addEventListener('shown.bs.modal', () => {
        const firstInput = modalRef.value.querySelector('input:not([type="hidden"]), select, textarea')
        if (firstInput) {
            firstInput.focus()
        }
    })

    loadData()
})
</script>

<template>
    <div class="container-fluid">

        <div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-4 px-3 px-md-0">
            <div>
                <h3 class="fw-bold mb-1">Konfiguracja eksportów</h3>
                <p class="text-muted small mb-0">Zarządzanie poświadczeniami dostępów do zewnętrznych portali ogłoszeniowych</p>
            </div>
        </div>

        <!-- Tabela z Paskiem Wyszukiwania -->
        <div class="card border-0 shadow-sm">
            <div class="card-body">
                <div class="row">
                    <div class="col-12 col-md-8"></div>
                    <div class="col-12 col-md-4">
                        <input v-model="searchValue" @input="handleSearch" type="text" class="form-control mb-3"
                            placeholder="Szukaj portalu lub loginu..." />
                    </div>
                </div>

                <!-- Tabela Vue3 Easy Data Table -->
                <div class="table-responsive">
                    <Vue3EasyDataTable v-model:server-options="serverOptions" :headers="headers" :items="items"
                        :loading="loading" :server-items-length="serverItemsLength" buttons-pagination
                        class="table-font-13" table-class-name="table table-hover align-middle custom-easy-table"
                        :rows-items="[10, 25, 50, 100]" empty-message="Brak skonfigurowanych portali"
                        rows-per-page-message="Wierszy na stronę:">

                        <template #loading>
                            <div class="py-4 text-center">
                                Pobieranie danych, proszę czekać...
                            </div>
                        </template>

                        <template #item-portalID="item">
                            <span class="   ">{{ item.portalID }}</span>
                        </template>

                        <template #item-name="item">
                            <span class="">{{ item.name }}</span>
                        </template>

                        <template #item-login="item">
                            <span v-if="item.login" class="">
                                {{ item.login }}
                            </span>
                            <span v-else class="text-muted fst-italic small">Brak skonfigurowanego loginu</span>
                        </template>

                        <!-- Customowa kolumna Akcje (Tylko Edycja) -->
                        <template #item-actions="item">
                            <div class="d-flex justify-content-end">
                                <button class="btn btn-sm btn-primary-light" title="Edytuj dostęp"
                                    @click="openEditModal(item.portalID)">
                                    <i class="bi bi-pencil"></i>
                                </button>
                            </div>
                        </template>
                    </Vue3EasyDataTable>
                </div>
            </div>
        </div>

        <!-- Modal Edycji Konfiguracji Portalu -->
        <div ref="modalRef" class="modal fade" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title fw-bold">{{ formTitle }}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>

                    <form ref="formRef" @submit.prevent="handleSubmit">
                        <div class="modal-body">
                            <input type="hidden" name="id" :value="formData.ID" />
                            <input type="hidden" name="portalID" :value="formData.portalID" />

                            <div class="mb-3">
                                <label class="form-label text-muted small">Nazwa portalu</label>
                                <input type="text" class="form-control bg-light" :value="formData.name" readonly disabled />
                            </div>

                            <div class="mb-3">
                                <label class="form-label fw-medium">Login / ID konta</label>
                                <input type="text" class="form-control" name="login" v-model="formData.login"
                                    placeholder="Wprowadź login lub ID integracji" required />
                            </div>

                            <div class="mb-3">
                                <label class="form-label fw-medium">Hasło / Token API</label>
                                <div class="input-group">
                                    <input :type="showPassword ? 'text' : 'password'" class="form-control" name="pass"
                                        v-model="formData.pass" placeholder="Wprowadź hasło lub klucz autoryzacyjny"
                                        autocomplete="off" />
                                    <button class="btn btn-outline-secondary" type="button"
                                        @click="showPassword = !showPassword" title="Pokaż/ukryj hasło">
                                        <i :class="['bi', showPassword ? 'bi-eye-slash' : 'bi-eye']"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button type="button" class="btn btn-light" data-bs-dismiss="modal">Anuluj</button>
                            <button type="submit" class="btn btn-primary">Zapisz konfigurację</button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    </div>

    <div id="glownyloader">
        <AppLoader :show="saving" :text="savingTXT" />
    </div>
</template>

<style scoped>
.table-responsive {
    display: block;
    width: 100%;
    overflow-x: auto;
    max-width: calc(100vw - 2rem) !important;
    -webkit-overflow-scrolling: touch;
}

:deep(.vue3-easy-data-table) {
    --easy-table-border: 1px solid #dee2e6;
    --easy-table-row-border: 1px solid #dee2e6;
    --easy-table-header-font-size: 0.85rem;
    --easy-table-header-height: 45px;
    --easy-table-body-row-height: 50px;
    width: 100% !important;
    max-width: 100% !important;
}

:deep(.vue3-easy-data-table__main) {
    width: 100% !important;
    overflow-x: auto !important;
}

.buttons-pagination .item.button.active {
    background-color: #043873 !important;
}
</style>